import React, { useEffect, useRef, useState, useCallback } from 'react'
import StatusPill from '../components/StatusPill'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { fmtDate, fmtDuration, hl, generateCustomerReport, isGeneralJob, isSuspensionJob, bookingJobSummary, bookingComponentDraft, JOB_FINANCIAL_SELECT, jobFinancials, jobModuleLabel, jobServiceScopeSummary, jobStatusMeta, normaliseJobStatus, rockShoxTrailheadUrl } from '../lib/utils'
import { LOYALTY_WORKSHOP_COLUMNS, loyaltyProgressForCustomer } from '../lib/loyalty'
import { buildServicePassportEntries } from '../lib/servicePassport'
import { generalServices, suspensionServices } from '../lib/serviceMenu'
import AddBikeModal from '../components/AddBikeModal'
import GeneralJobModal from '../components/GeneralJobModal'
import { canRunJobLifecycleAction } from '../lib/jobLifecycle'
import { customerDisplayName, customerJobActivity, loadWorkshopRows, sortCustomers } from '../lib/customerDirectory'
import CustomerModal from '../components/CustomerModal'
import CustomerFileAccessModal from '../components/CustomerFileAccessModal'
import ComponentModal from '../components/ComponentModal'
import JobModal from '../components/JobModal'
import NewJobIntakeModal from '../components/NewJobIntakeModal'
import ViewJobModal from '../components/ViewJobModal'
import LegacyHistoricJobModal from '../components/LegacyHistoricJobModal'
import { loadHistoricJobRecords } from '../lib/legacyHistoricJobs'
import { SetupRecordSummary, setupTypeLabel } from '../components/SetupRecordForm'

const CUSTOMER_JOB_GROUPS = [
  { key: 'pending', label: 'Pending Service Jobs' },
  { key: 'in_process', label: 'In Process' },
  { key: 'completed', label: 'Completed' },
]

function customerJobGroup(job) {
  const status = normaliseJobStatus(job.status)
  if (status === 'completed' || status === 'cancelled') return 'completed'
  if (isGeneralJob(job)) {
    return ['awaiting_customer_item', 'estimate_sent', 'awaiting_parts'].includes(status) ? 'pending' : 'in_process'
  }
  if (!job.component_id || status === 'awaiting_customer_item' || status === 'estimate_sent' || status === 'awaiting_parts') return 'pending'
  return 'in_process'
}

function groupedCustomerJobs(jobRows) {
  return jobRows.reduce((groups, job) => {
    groups[customerJobGroup(job)].push(job)
    return groups
  }, { pending: [], in_process: [], completed: [] })
}

export default function Customers() {
  const [searchParams] = useSearchParams()
  const { workshopId, workshop, profile } = useAuth()
  const localTestMode = process.env.REACT_APP_LOCAL_TEST_MODE === 'true'
  const [customers, setCustomers] = useState([])
  const [bikes, setBikes] = useState([])
  const [components, setComponents] = useState([])
  const [jobs, setJobs] = useState([])
  const [legacyRecords, setLegacyRecords] = useState([])
  const [historicJobs, setHistoricJobs] = useState([])
  const [historicLoadError, setHistoricLoadError] = useState('')
  const [historicLoading, setHistoricLoading] = useState(false)
  const [viewHistoricId, setViewHistoricId] = useState(null)
  const [loyaltyRewards, setLoyaltyRewards] = useState([])
  const [loyaltyWorkshop, setLoyaltyWorkshop] = useState(workshop || {})
  const [services, setServices] = useState([])
  const [generalJobServices, setGeneralJobServices] = useState([])
  const [setupRecords, setSetupRecords] = useState([])
  const [portalAccess, setPortalAccess] = useState([])
  const [portalBusy, setPortalBusy] = useState('')
  const [portalMsg, setPortalMsg] = useState('')
  const [portalError, setPortalError] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const loadedRef = useRef(false)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('name_asc')
  const [openJobsOnly, setOpenJobsOnly] = useState(false)
  const [addBikeCustomer, setAddBikeCustomer] = useState(null)
  const activeBikeScope = useRef(null)
  activeBikeScope.current = { workshopId, customerId: addBikeCustomer?.id }
  useEffect(() => () => { activeBikeScope.current = null }, [])
  const [jobChoice, setJobChoice] = useState(null)
  const [generalIntake, setGeneralIntake] = useState(null)
  const canCreateJob = canRunJobLifecycleAction(profile, 'create')
  const generalEnabled = workshop?.general_workshop_enabled === true
  const loadVersion = useRef(0)
  const [expandedCustomerId, setExpandedCustomerId] = useState(null)
  const [selectedBikeByCustomer, setSelectedBikeByCustomer] = useState({})
  const [componentLinkError, setComponentLinkError] = useState(null)

  // Modal state — null = closed, object/id = open
  const [customerModal, setCustomerModal] = useState(null)  // null | 'new' | customer object
  const [fileAccessCustomer, setFileAccessCustomer] = useState(null)
  const [compModal, setCompModal] = useState(null)           // null | { customerId } | component object
  const [intakeModal, setIntakeModal] = useState(null)       // null | { customerId, componentId }
  const [jobModal, setJobModal] = useState(null)             // null | existing job object
  const [viewJobId, setViewJobId] = useState(null)
  const requestedCustomerId = searchParams.get('customer') || ''

  const load = useCallback(async () => {
    if (!workshopId) return
    const version = ++loadVersion.current
    if (!loadedRef.current) setLoading(true)
    try {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    const portalPromise = localTestMode
      ? Promise.resolve({ access: [] })
      : token
      ? fetch('/.netlify/functions/customer-portal-access', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'list', workshopId }),
        }).then(async res => {
          const payload = await res.json().catch(() => ({}))
          if (!res.ok) throw new Error(payload.error || 'Could not load portal access.')
          return payload
        }).catch(err => ({ access: [], error: err.message }))
      : Promise.resolve({ access: [] })
    const [c, b, co, j, lr, rw, ws, j2, sr, pa] = await Promise.all([
      loadWorkshopRows(supabase, 'customers', workshopId).then(data => ({ data })),
      loadWorkshopRows(supabase, 'bikes', workshopId).then(data => ({ data })),
      loadWorkshopRows(supabase, 'components', workshopId).then(data => ({ data })),
      loadWorkshopRows(supabase, 'jobs', workshopId, JOB_FINANCIAL_SELECT).then(data => ({ data: data.sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0)) })),
      supabase
        .from('legacy_service_records')
        .select('*, bike:bikes(brand,model,year,serial), component:components(type,brand,model,serial)')
        .eq('workshop_id', workshopId)
        .order('service_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false }),
      supabase.from('loyalty_rewards').select('*').eq('workshop_id', workshopId).order('created_at', { ascending: false }),
      supabase.from('workshops').select(`${LOYALTY_WORKSHOP_COLUMNS}, service_passport_enabled`).eq('id', workshopId).single(),
      supabase.from('services').select('*').eq('workshop_id', workshopId).order('sort_order'),
      supabase
        .from('setup_snapshots')
        .select('*')
        .eq('workshop_id', workshopId)
        .order('is_active', { ascending: false })
        .order('updated_at', { ascending: false }),
      portalPromise,
    ])
    if (version !== loadVersion.current) return
    const failed = [c, b, co, j, lr, rw, ws, j2, sr].find(result => result?.error)
    if (failed?.error) throw failed.error
    setCustomers(c.data || [])
    setBikes(b.data || [])
    setComponents(co.data || [])
    let jobRows = j.data || []
    if (jobRows.length) {
      const { data: jobServices } = await supabase
        .from('job_services')
        .select('id, job_id, name, category, is_addon')
        .eq('workshop_id', workshopId)
      if (Array.isArray(jobServices)) {
        const servicesByJobId = jobServices.reduce((groups, row) => {
          if (!groups[row.job_id]) groups[row.job_id] = []
          groups[row.job_id].push(row)
          return groups
        }, {})
        jobRows = jobRows.map(job => ({ ...job, job_services: servicesByJobId[job.id] || [] }))
      }
    }
    if (version !== loadVersion.current) return
    setJobs(jobRows)
    setLegacyRecords(lr?.data || [])
    setLoyaltyRewards(rw?.data || [])
    setLoyaltyWorkshop({ ...(workshop || {}), ...(ws?.data || {}) })
    const serviceRows = j2?.data || []
    setServices(suspensionServices(serviceRows))
    setGeneralJobServices(generalServices(serviceRows))
    setSetupRecords(sr?.data || [])
    setPortalAccess(pa?.access || [])
    if (pa?.error) setPortalError(pa.error)
    setLoadError('')
    loadedRef.current = true
    } catch (err) {
      if (version === loadVersion.current) setLoadError(err.message || 'Could not refresh customers.')
    } finally {
    if (version === loadVersion.current) setLoading(false)
    }
  }, [workshopId, workshop, localTestMode])

  const loadHistory = useCallback(async () => {
    if (!workshopId) return
    setHistoricLoading(true)
    setHistoricLoadError('')
    try {
      setHistoricJobs(await loadHistoricJobRecords(supabase, workshopId))
    } catch (error) {
      setHistoricLoadError(error.message || 'Historical jobs could not be loaded.')
    } finally {
      setHistoricLoading(false)
    }
  }, [workshopId])

  useEffect(() => {
    if (!requestedCustomerId) return
    if (!customers.some(customer => customer.id === requestedCustomerId)) return
    setSearch('')
    setOpenJobsOnly(false)
    setExpandedCustomerId(requestedCustomerId)
  }, [requestedCustomerId, customers])

  useEffect(() => {
    loadedRef.current = false
    setCustomers([]); setBikes([]); setComponents([]); setJobs([])
    setAddBikeCustomer(null); setJobChoice(null); setGeneralIntake(null); setIntakeModal(null)
    setSelectedBikeByCustomer({}); setExpandedCustomerId(null)
    return () => { loadVersion.current += 1 }
  }, [workshopId])
  useEffect(() => { load() }, [load])
  useEffect(() => { loadHistory() }, [loadHistory])

  const q = search.trim().toLowerCase()
  function bikeProfile(customer) {
    return [customer.bike_year, customer.bike_brand, customer.bike_model].filter(Boolean).join(' ')
  }

  function bikeSetupMeta(customer) {
    return [
      customer.bike_type ? `Category: ${customer.bike_type}` : null,
      customer.bike_size ? `Size: ${customer.bike_size}` : null,
      customer.wheel_setup ? `Wheel: ${customer.wheel_setup}` : null,
    ].filter(Boolean).join(' - ')
  }

  function bikeName(bike) {
    return [bike.year, bike.brand, bike.model].filter(Boolean).join(' ') || 'Bike'
  }

  function bikeMeta(bike) {
    return [
      bike.bike_type ? `Category: ${bike.bike_type}` : null,
      bike.bike_size ? `Size: ${bike.bike_size}` : null,
      bike.wheel_setup ? `Wheel: ${bike.wheel_setup}` : null,
    ].filter(Boolean).join(' - ')
  }

  function verificationLabel(value) {
    if (value === 'profile_fields') return 'Customer bike details'
    if (value === 'customer_submitted') return 'Customer submitted'
    if (value === 'workshop_verified') return 'Workshop verified'
    if (value === 'needs_review') return 'Needs review'
    if (value === 'rejected') return 'Rejected'
    if (value === 'superseded') return 'Superseded'
    return value ? String(value).replace(/_/g, ' ') : 'Workshop verified'
  }

  function customerBikes(customer) {
    const rows = bikes
      .filter(bike => bike.customer_id === customer.id)
      .sort((a, b) => {
        const av = a.verification_state === 'workshop_verified' ? 0 : 1
        const bv = b.verification_state === 'workshop_verified' ? 0 : 1
        if (av !== bv) return av - bv
        return new Date(a.created_at || 0) - new Date(b.created_at || 0)
      })
    if (rows.length) return rows
    if (!bikeProfile(customer) && !bikeSetupMeta(customer)) return []
    return [{
      id: `legacy:${customer.id}`,
      customer_id: customer.id,
      brand: customer.bike_brand || '',
      model: customer.bike_model || '',
      year: customer.bike_year || '',
      bike_type: customer.bike_type || '',
      bike_size: customer.bike_size || '',
      wheel_setup: customer.wheel_setup || '',
      verification_state: 'profile_fields',
      legacy: true,
    }]
  }

  function selectedBikeForCustomer(customer) {
    const rows = customerBikes(customer)
    const selectedId = selectedBikeByCustomer[customer.id]
    return rows.find(bike => bike.id === selectedId) || rows[0] || null
  }

  function componentBelongsToBike(component, selectedBike, bikeRows) {
    if (!selectedBike) return true
    if (selectedBike.legacy) return !component.bike_id
    if (component.bike_id === selectedBike.id) return true
    return bikeRows.length === 1 && !component.bike_id
  }

  function registeredBikeId(customerId, bikeId) {
    return bikes.some(bike => bike.id === bikeId && bike.customer_id === customerId) ? bikeId : ''
  }

  function repeatIntakeSeed(customer, job = null, component = null, selectedBike = null) {
    const linkedComponent = component
      || components.find(row => row.id === job?.component_id && row.customer_id === customer.id)
      || null
    const componentBikeId = registeredBikeId(customer.id, linkedComponent?.bike_id)
    const jobBikeId = registeredBikeId(customer.id, job?.bike_id)
    const selectedBikeId = selectedBike?.legacy ? '' : registeredBikeId(customer.id, selectedBike?.id)
    const bikeId = componentBikeId || jobBikeId || (!linkedComponent ? selectedBikeId : '')

    return {
      customerId: customer.id,
      bikeId,
      componentId: linkedComponent?.id || '',
      serviceIds: job?.service_ids || [],
      requestedWork: job ? (jobServiceScopeSummary(job) || bookingJobSummary(job) || job.service_summary || '') : '',
    }
  }

  function openAddComponent(customer, selectedBike) {
    const prefillData = selectedBike && !selectedBike.legacy ? { bike_id: selectedBike.id } : null
    setCompModal({ customerId: customer.id, prefillData })
  }

  const activity = customerJobActivity(jobs)
  const filtered = sortCustomers(customers.filter(c => (!openJobsOnly || activity.get(c.id)?.open) && (
    !q ||
    (c.first_name + ' ' + c.last_name).toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, '')) ||
    bikeProfile(c).toLowerCase().includes(q) ||
    bikeSetupMeta(c).toLowerCase().includes(q) ||
    bikes.some(bike => bike.customer_id === c.id && `${bikeName(bike)} ${bikeMeta(bike)}`.toLowerCase().includes(q)) ||
    legacyRecords.some(record => record.customer_id === c.id && (record.search_text || '').toLowerCase().includes(q))
  )), sort, activity)

  const viewJob = jobs.find(j => j.id === viewJobId)
  const viewComp = viewJob ? components.find(c => c.id === viewJob.component_id) : null
  const viewCust = viewJob ? customers.find(c => c.id === viewJob.customer_id) : null
  const viewBike = viewJob ? bikes.find(b => b.id === viewJob.bike_id) : null

  if (loading && !loadedRef.current) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>
  if (loadError && !loadedRef.current) return (
    <div className="empty">
      <p>{loadError}</p>
      <button className="btn btn-primary" onClick={load}>Retry</button>
    </div>
  )

  // Derive modal props
  const isEditCustomer = customerModal && customerModal !== 'new'
  const isEditComp = compModal && compModal.id  // has an id = it's an existing component
  const isEditJob = jobModal && jobModal.id      // has an id = it's an existing job

  function createComponentFromJob(customerId, job) {
    if (!isSuspensionJob(job)) {
      setComponentLinkError('General Workshop jobs do not use the suspension component workflow yet.')
      return
    }
    setComponentLinkError(null)
    setCompModal({
      customerId,
      jobId: job.id,
      prefillData: bookingComponentDraft(job),
    })
  }

  async function handleComponentSaved(component) {
    const jobId = compModal && !compModal.id ? compModal.jobId : null
    setComponentLinkError(null)

    if (jobId && component?.id) {
      const { error } = await supabase
        .from('jobs')
        .update({ component_id: component.id })
        .eq('id', jobId)
      if (error) setComponentLinkError(error.message)
    }

    setCompModal(null)
    await load()
  }

  function portalAccessForCustomer(customerId) {
    const rows = portalAccess.filter(row => row.customer_id === customerId)
    return rows.find(row => ['pending', 'active'].includes(row.status)) || rows[0] || null
  }

  function portalStatusMeta(access) {
    if (!access) return { label: 'No portal access', tone: 'muted' }
    if (access.requires_review) return { label: 'Portal access needs renewal', tone: 'orange' }
    if (access.status === 'active') return { label: 'Portal active', tone: 'green' }
    if (access.status === 'pending') return { label: 'Portal pending', tone: 'orange' }
    if (access.status === 'revoked') return { label: 'Portal revoked', tone: 'muted' }
    if (access.status === 'expired') return { label: 'Portal expired', tone: 'orange' }
    return { label: access.status || 'Portal access', tone: 'muted' }
  }

  async function runPortalAction(action, customer, access = null) {
    if (localTestMode) {
      setPortalError('Portal access messages are disabled in local test mode.')
      return
    }
    setPortalBusy(`${action}:${customer.id}`)
    setPortalMsg('')
    setPortalError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before changing portal access.')

      const res = await fetch('/.netlify/functions/customer-portal-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action,
          workshopId,
          customerId: customer.id,
          accessId: access?.id || '',
        }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Portal access action failed.')
      setPortalMsg(action === 'revoke'
        ? `Portal access revoked for ${customer.first_name} ${customer.last_name}.`
        : `Portal access email sent to ${customer.email}.`)
      await load()
    } catch (err) {
      setPortalError(err.message || 'Portal access action failed.')
    } finally {
      setPortalBusy('')
    }
  }

  function renderPortalAccessControls(customer) {
    const access = portalAccessForCustomer(customer.id)
    const meta = portalStatusMeta(access)
    const busyIssue = portalBusy === `issue:${customer.id}`
    const busyResend = portalBusy === `resend:${customer.id}`
    const busyRevoke = portalBusy === `revoke:${customer.id}`

    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        {access?.requires_review && <span className="fs12 text-muted">
          {access.status === 'active' ? 'Check the customer email, then revoke and issue new access.' : 'Check the customer email, then resend access.'}
        </span>}
        {(!access || ['revoked', 'expired'].includes(access.status)) && (
          <button className="btn btn-ghost btn-sm" onClick={() => runPortalAction('issue', customer)} disabled={busyIssue || localTestMode} title={localTestMode ? 'Outbound portal messages are disabled in local test mode.' : undefined}>
            {busyIssue ? 'Sending...' : 'Issue Access'}
          </button>
        )}
        {access?.status === 'pending' && (
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => runPortalAction('resend', customer, access)} disabled={busyResend || localTestMode} title={localTestMode ? 'Outbound portal messages are disabled in local test mode.' : undefined}>
              {busyResend ? 'Sending...' : 'Resend'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => runPortalAction('revoke', customer, access)} disabled={busyRevoke || localTestMode} title={localTestMode ? 'Portal changes are disabled in local test mode.' : undefined}>
              {busyRevoke ? 'Revoking...' : 'Revoke'}
            </button>
          </>
        )}
        {access?.status === 'active' && (
          <button className="btn btn-ghost btn-sm" onClick={() => runPortalAction('revoke', customer, access)} disabled={busyRevoke || localTestMode} title={localTestMode ? 'Portal changes are disabled in local test mode.' : undefined}>
            {busyRevoke ? 'Revoking...' : 'Revoke'}
          </button>
        )}
      </div>
    )
  }

  function legacyRowsForCustomer(customerId) {
    return legacyRecords
      .filter(record => record.customer_id === customerId)
      .sort((a, b) => {
        const ad = a.service_date || a.created_at || ''
        const bd = b.service_date || b.created_at || ''
        return bd.localeCompare(ad)
      })
  }

  function legacyServiceTitle(record) {
    return record.service_title || record.service_description || 'Legacy service'
  }

  function legacyAssetLabel(record) {
    const bike = record.bike ? bikeName(record.bike) : ''
    const component = record.component ? [record.component.brand, record.component.model].filter(Boolean).join(' ') : ''
    return [bike, component].filter(Boolean).join(' - ')
  }

  function renderLegacyHistory(customer) {
    const rows = legacyRowsForCustomer(customer.id)
    if (!rows.length) return null

    return (
      <div className="legacy-customer-history">
        <div className="legacy-history-section-head">
          <div>
            <span className="fs11 text-muted fw7">Imported legacy history</span>
            <div className="text-muted fs12">Reference-only service records imported from old systems.</div>
          </div>
          <span className="customer-summary-chip"><strong>{rows.length}</strong> legacy record{rows.length === 1 ? '' : 's'}</span>
        </div>
        <div className="legacy-history-list compact">
          {rows.slice(0, 5).map(record => (
            <div key={record.id} className="legacy-history-row compact">
              <div>
                <div className="legacy-history-title">
                  <span>{record.service_date ? fmtDate(record.service_date) : record.service_date_text || 'No date'}</span>
                  <strong>{legacyServiceTitle(record)}</strong>
                  <span className="job-status-chip job-status-muted">Legacy</span>
                </div>
                <div className="legacy-history-meta">
                  {legacyAssetLabel(record) && <span>{legacyAssetLabel(record)}</span>}
                  {record.source_system && <span>{record.source_system}</span>}
                  {record.source_reference && <span>{record.source_reference}</span>}
                </div>
                {(record.service_description || record.notes || record.setup_text) && (
                  <div className="legacy-history-copy">{[record.service_description, record.notes, record.setup_text].filter(Boolean).join(' - ')}</div>
                )}
              </div>
              {record.amount_charged !== null && record.amount_charged !== undefined && (
                <div className="legacy-history-amount">{record.currency || 'GBP'} {Number(record.amount_charged).toFixed(2)}</div>
              )}
            </div>
          ))}
        </div>
        {rows.length > 5 && <div className="text-muted fs11 mt4">Showing latest 5 legacy records. Use Import to search the full history.</div>}
      </div>
    )
  }

  function renderCustomerJobRow(job, customer, component) {
    const generalJob = isGeneralJob(job)
    const financials = jobFinancials(job)
    const selectedServices = jobServiceScopeSummary(job)
    const status = jobStatusMeta(job)
    const summary = selectedServices || bookingJobSummary(job) || job.kit || 'No service selected'
    const technician = job.technician || (customerJobGroup(job) === 'pending' ? 'Pending' : '-')

    return (
      <div key={job.id} className="job-row">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="fs12" style={{ color: 'var(--light)' }}>
              {fmtDate(job.date)} - {technician}{job.duration_mins ? ' - ' + fmtDuration(job.duration_mins) : ''}
            </span>
            {generalJob && <span className="job-status-chip job-status-blue">{jobModuleLabel(job)}</span>}
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </div>
          <div className="fs12 text-muted" style={{ marginTop: 3 }}>{summary}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <div className="fs12 text-white fw7">GBP {financials.totalCost.toFixed(2)}</div>
            <div className={component ? 'fs11 text-green' : 'fs11 text-muted'}>
              {component ? `${financials.marginPercent.toFixed(0)}% margin` : generalJob ? 'General job' : 'No component'}
            </div>
          </div>
          {!generalJob && !component && (
            <button className="btn btn-green btn-sm" onClick={() => createComponentFromJob(customer.id, job)}>Create Component</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setJobModal(job)}>Open job</button>
          {!generalJob && (
            <button className="btn btn-ghost btn-sm" disabled={!canCreateJob} onClick={() => setIntakeModal(repeatIntakeSeed(customer, job, component))}>Book repeat work</button>
          )}
          {generalJob && (
            <button className="btn btn-green btn-sm" onClick={() => setViewJobId(job.id)}>Report</button>
          )}
          {!generalJob && component && (
            <button className="btn btn-green btn-sm" onClick={() => generateCustomerReport(job, customer, component, workshop)}>Report</button>
          )}
        </div>
      </div>
    )
  }

  function renderCustomerJobGroups(groupedJobs, customer, component = null) {
    return CUSTOMER_JOB_GROUPS.map((group, index) => {
      const rows = groupedJobs[group.key]
      if (!rows.length) return null
      return (
        <div
          key={group.key}
          style={{
            marginTop: index === 0 ? 0 : 10,
            paddingTop: index === 0 ? 0 : 10,
            borderTop: index === 0 ? 'none' : '1px solid var(--border)',
          }}
        >
          <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{group.label}</div>
          {rows.map(job => renderCustomerJobRow(job, customer, component))}
        </div>
      )
    })
  }

  function setupRecordsForComponent(componentId) {
    return setupRecords.filter(record => record.component_id === componentId)
  }

  function renderComponentSetup(component) {
    const componentType = String(component.type || '').toLowerCase()
    if (!['fork', 'shock'].includes(componentType)) return null

    const rows = setupRecordsForComponent(component.id)
    const active = rows.find(record => record.setup_type === 'workshop_current' && record.is_active)
      || rows.find(record => record.setup_type === 'workshop_current')
      || rows[0]
    const riderVariants = rows.filter(record => record.setup_type === 'rider_variant')

    if (!active && !riderVariants.length) {
      return (
        <div className="job-modal-callout" style={{ marginTop: 12 }}>
          <div className="job-modal-callout-title">Component Setup</div>
          <div className="text-muted fs12">
            No verified setup recorded yet. Saving after-service settings on a service job will add the workshop current setup here.
          </div>
        </div>
      )
    }

    return (
      <div className="job-modal-callout" style={{ marginTop: 12 }}>
        <div className="section-head compact" style={{ marginBottom: 8 }}>
          <div>
            <div className="job-modal-callout-title">Component Setup</div>
            <div className="text-muted fs12">
              {active ? `${setupTypeLabel(active.setup_type)} - ${active.name || 'Current setup'}` : 'No workshop current setup yet'}
            </div>
          </div>
          <div className="action-row">
            {active?.is_active ? <StatusPill tone="green">Workshop Verified</StatusPill> : null}
            {riderVariants.length ? <span className="job-status-chip job-status-blue">{riderVariants.length} rider variant{riderVariants.length === 1 ? '' : 's'}</span> : null}
          </div>
        </div>
        {active ? <SetupRecordSummary record={active} /> : null}
      </div>
    )
  }

  function passportEntriesForComponent(component, componentJobs) {
    return buildServicePassportEntries({
      workshop: loyaltyWorkshop,
      jobs: componentJobs,
      components: [component],
    })
  }

  function renderServicePassportPreview(component, componentJobs) {
    const entries = passportEntriesForComponent(component, componentJobs)
    if (!loyaltyWorkshop.service_passport_enabled || !entries.length) return null

    return (
      <div className="service-passport-preview">
        <div className="service-passport-preview-head">
          <div>
            <div className="job-modal-callout-title">Service Passport Preview</div>
            <div className="text-muted fs12">Minimal completed and paid suspension history customers can download from the rider portal.</div>
          </div>
          <span className="job-status-chip job-status-blue">{entries.length} passport entr{entries.length === 1 ? 'y' : 'ies'}</span>
        </div>
        <div className="service-passport-entry-list compact">
          {entries.slice(0, 3).map(entry => (
            <div key={entry.job_id} className="service-passport-entry compact">
              <div>
                <strong>{entry.service_name}</strong>
                <span>{fmtDate(entry.service_date)} - {entry.service_type}</span>
                {entry.upgrades_installed.length ? (
                  <small>Upgrades: {entry.upgrades_installed.join(', ')}</small>
                ) : <small>No add-on upgrades recorded</small>}
              </div>
            </div>
          ))}
        </div>
        {entries.length > 3 ? <div className="text-muted fs11 mt4">Showing latest 3 passport entries.</div> : null}
      </div>
    )
  }

  return (
    <>
      <div className="page-head customers-refresh-head">
        <div>
          <div className="refresh-kicker">Customer records</div>
          <div className="page-title">Customers</div>
          <div className="text-muted fs12 mt4">Bikes, components and service history connected to repeat work.</div>
        </div>
        <div className="page-actions">
        {canCreateJob && <button className="btn btn-ghost" onClick={() => setIntakeModal({})}>Quick intake</button>}
        <button className="btn btn-primary" onClick={() => setCustomerModal('new')}>+ Add Customer</button>
        </div>
      </div>

      <div className="search-wrap">
        <span className="search-icon">🔍</span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or mobile number…" />
        {search && <button className="search-clear" onClick={() => setSearch('')}>×</button>}
      </div>

      <div className="customer-browse-controls">
        <label>Sort customers<select value={sort} onChange={e => setSort(e.target.value)}>
          <option value="name_asc">Name A-Z</option><option value="name_desc">Name Z-A</option>
          <option value="recent_job">Most recent job</option><option value="recent_added">Recently added</option>
        </select></label>
        <label><input type="checkbox" checked={openJobsOnly} onChange={e => setOpenJobsOnly(e.target.checked)} />Customers with open jobs</label>
      </div>
      {(q || openJobsOnly) && <div className="result-count">{filtered.length} of {customers.length} customer{customers.length !== 1 ? 's' : ''} found</div>}
      {componentLinkError && <div className="alert alert-error">{componentLinkError}</div>}
      {loadError && loadedRef.current && <div className="alert alert-error refresh-inline-error">Customer refresh failed: {loadError} <button className="btn btn-ghost btn-sm" onClick={load}>Retry</button></div>}
      {historicLoadError && <div className="alert alert-error" role="alert">
        Historical jobs could not be loaded: {historicLoadError}. Customer records remain available.
        <button type="button" className="btn btn-ghost btn-sm" onClick={loadHistory}>Retry history</button>
      </div>}
      {historicLoading && <div className="text-muted fs12">Loading historical jobs...</div>}
      {portalMsg && <div className="alert alert-success">{portalMsg}</div>}
      {portalError && <div className="alert alert-error">{portalError}</div>}

      {!filtered.length ? (
        <div className="empty"><div className="empty-icon">{q ? '🔍' : '👤'}</div><p>{openJobsOnly ? 'No customers with open jobs match these filters.' : q ? 'No customers match that search.' : 'No customers yet. Add your first customer to get started.'}</p></div>
      ) : (
        <div className="customer-list">
        {filtered.map(c => {
        const comps = components.filter(x => x.customer_id === c.id)
        const custJobs = jobs.filter(j => j.customer_id === c.id)
        const custLegacyRecords = legacyRowsForCustomer(c.id)
        const custHistoricJobs = historicJobs.filter(record => record.customer_id === c.id)
        const componentIds = new Set(comps.map(comp => comp.id))
        const bikeRows = customerBikes(c)
        const hasProfileBikeFallback = bikeRows.length === 1 && bikeRows[0].legacy
        const bikeMetricLabel = hasProfileBikeFallback ? 'bike details' : `bike${bikeRows.length !== 1 ? 's' : ''}`
        const selectedBike = selectedBikeForCustomer(c)
        const visibleComps = comps.filter(comp => componentBelongsToBike(comp, selectedBike, bikeRows))
        const generalJobs = custJobs
          .filter(isGeneralJob)
          .filter(j => !selectedBike || !j.bike_id || selectedBike.legacy || j.bike_id === selectedBike.id)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
        const unassignedJobs = custJobs
          .filter(isSuspensionJob)
          .filter(j => !j.component_id || !componentIds.has(j.component_id))
          .filter(j => !selectedBike || !j.bike_id || selectedBike.legacy || j.bike_id === selectedBike.id)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
        const spend = custJobs.reduce((s, j) => s + jobFinancials(j).totalCost, 0)
        const latestJob = [...custJobs].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))[0]
        const portalMeta = portalStatusMeta(portalAccessForCustomer(c.id))
        const loyaltyProgress = loyaltyProgressForCustomer({
          workshop: loyaltyWorkshop,
          jobs: custJobs,
          components,
          rewards: loyaltyRewards,
          customerId: c.id,
        })
        const isExpanded = expandedCustomerId === c.id

        return (
          <div key={c.id} className={`customer-list-item ${isExpanded ? 'is-open' : ''}`}>
            <button
              type="button"
              className="customer-list-row"
              onClick={() => setExpandedCustomerId(current => current === c.id ? null : c.id)}
            >
              <span className="customer-list-main">
                <span className="customer-list-name" dangerouslySetInnerHTML={{ __html: hl(customerDisplayName(c), q) }} />
                <span className="customer-list-contact" dangerouslySetInnerHTML={{ __html: hl(c.email || '', q) + (c.email && c.phone ? ' - ' : '') + hl(c.phone || '', q) }} />
                {latestJob && <span className="customer-list-activity">Last job {fmtDate(latestJob.date)}</span>}
              </span>
              <span className="customer-list-metrics">
                <span><strong>{bikeRows.length}</strong> {bikeMetricLabel}</span>
                <span><strong>{comps.length}</strong> components</span>
                <span><strong>{custJobs.length}</strong> jobs</span>
                <span><strong>{custLegacyRecords.length}</strong> legacy</span>
                <span><strong>{custHistoricJobs.length}</strong> legacy jobs</span>
                <span><strong>GBP {spend.toFixed(0)}</strong> spend</span>
                {loyaltyProgress.enabled && (
                  <StatusPill tone={loyaltyProgress.availableCount > 0 ? 'green' : 'muted'}>
                    {loyaltyProgress.availableCount > 0 ? 'Loyalty earned' : `${loyaltyProgress.qualifiedCount}/${loyaltyProgress.required} loyalty`}
                  </StatusPill>
                )}
                <StatusPill tone={portalMeta.tone}>{portalMeta.label}</StatusPill>
              </span>
              <span className="customer-list-toggle">{isExpanded ? 'Close' : 'Open'}</span>
            </button>

            {isExpanded && (
            <div className="customer-detail-panel">
            <div className="customer-card-head">
              <div>
                <div style={{ fontSize: 17, fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: hl(c.first_name + ' ' + c.last_name, q) }} />
                {bikeRows.length > 1 ? (
                  <div className="customer-bike-selector">
                    <label>Bike</label>
                    <select
                      value={selectedBike?.id || ''}
                      onChange={e => setSelectedBikeByCustomer(current => ({ ...current, [c.id]: e.target.value }))}
                    >
                      {bikeRows.map(bike => <option key={bike.id} value={bike.id}>{bikeName(bike)}</option>)}
                    </select>
                  </div>
                ) : selectedBike ? (
                  <div className="text-muted fs12 mt4">{selectedBike.legacy ? 'Customer bike details' : 'Bike'}: {bikeName(selectedBike)}</div>
                ) : null}
                {selectedBike && <div className="text-muted fs12 mt4">{bikeMeta(selectedBike)}</div>}
                {selectedBike && <div className="fs11 mt4"><StatusPill tone="muted">{verificationLabel(selectedBike.verification_state)}</StatusPill></div>}
                <div className="text-muted fs12 mt4" dangerouslySetInnerHTML={{ __html: hl(c.email || '', q) + (c.email && c.phone ? ' · ' : '') + hl(c.phone || '', q) }} />
                {c.notes && <div className="text-muted fs12 mt4">{c.notes}</div>}
              </div>
              <div className="customer-summary">
                <span className="customer-summary-chip"><strong>{comps.length}</strong> component{comps.length !== 1 ? 's' : ''}</span>
                <span className="customer-summary-chip"><strong>{bikeRows.length}</strong> {bikeMetricLabel}</span>
                <span className="customer-summary-chip"><strong>{custJobs.length}</strong> job{custJobs.length !== 1 ? 's' : ''}</span>
                <span className="customer-summary-chip"><strong>{custLegacyRecords.length}</strong> legacy</span>
                <span className="customer-summary-chip"><strong>{custHistoricJobs.length}</strong> legacy jobs</span>
                <span className="customer-summary-chip"><strong className="text-green">£{spend.toFixed(2)}</strong> spend</span>
                {loyaltyProgress.enabled && (
                  <span className="customer-summary-chip">
                    <strong className={loyaltyProgress.availableCount > 0 ? 'text-green' : ''}>
                      {loyaltyProgress.availableCount > 0 ? loyaltyProgress.availableCount : `${loyaltyProgress.qualifiedCount}/${loyaltyProgress.required}`}
                    </strong> loyalty
                  </span>
                )}
                {renderPortalAccessControls(c)}
                {profile?.role === 'owner' && <button className="btn btn-ghost btn-sm" onClick={() => setFileAccessCustomer(c)}>File access</button>}
                <button className="btn btn-ghost btn-sm" onClick={() => setCustomerModal(c)}>Edit</button>
                {canCreateJob && <button className="btn btn-ghost btn-sm" onClick={() => setAddBikeCustomer(c)}>+ Add Bike</button>}
                <button className="btn btn-ghost btn-sm" onClick={() => openAddComponent(c, selectedBike)}>+ Add Component</button>
                {canCreateJob && <button className="btn btn-green btn-sm" onClick={() => setJobChoice({ customer: c, bike: selectedBike?.legacy ? null : selectedBike })}>+ Service Job</button>}
              </div>
            </div>

            {loyaltyProgress.enabled && (
              <div className="alert alert-info" style={{ marginTop: 12 }}>
                Loyalty: {loyaltyProgress.availableCount > 0
                  ? `${loyaltyProgress.availableCount} reward${loyaltyProgress.availableCount === 1 ? '' : 's'} available (${loyaltyProgress.rewardLabel}).`
                  : `${loyaltyProgress.qualifiedCount}/${loyaltyProgress.required} completed paid eligible services toward ${loyaltyProgress.rewardLabel}.`}
              </div>
            )}

            {selectedBike && (
              <div className="customer-bike-panel">
                <div>
                  <span className="fs11 text-muted fw7">{selectedBike.legacy ? 'Customer bike details' : 'Selected bike'}</span>
                  <div className="text-white fw7">{bikeName(selectedBike)}</div>
                  {bikeMeta(selectedBike) && <div className="text-muted fs12">{bikeMeta(selectedBike)}</div>}
                </div>
                <div className="action-row">
                  <StatusPill tone="muted">{verificationLabel(selectedBike.verification_state)}</StatusPill>
                  {!selectedBike.legacy && <span className="customer-summary-chip"><strong>{visibleComps.length}</strong> component{visibleComps.length === 1 ? '' : 's'}</span>}
                </div>
              </div>
            )}

            {custHistoricJobs.length > 0 && <section className="customer-legacy-jobs">
              <div className="sec">Legacy Jobs ({custHistoricJobs.length})</div>
              <div className="field-hint">Previous operating system references. Not active jobs or loyalty/payment history.</div>
              {custHistoricJobs.map(record => <button type="button" key={record.id}
                className="jobs-historic-row" onClick={() => setViewHistoricId(record.id)}>
                <span className="mono">{record.source_reference}</span>
                <span>{record.facts?.bikes || 'Bike not supplied'}</span>
                <span>{record.facts?.created || record.facts?.scheduled || 'Date not supplied'}</span>
                <span className="job-status-chip job-status-muted">Legacy / previous operating system</span>
                <span>Open historical detail</span>
              </button>)}
            </section>}

            {renderLegacyHistory(c)}

            {visibleComps.length === 0 && unassignedJobs.length === 0 && generalJobs.length === 0 ? (
              <div className="muted-empty">No components registered yet.</div>
            ) : visibleComps.map(comp => {
              const compJobs = jobs.filter(j => isSuspensionJob(j) && j.component_id === comp.id).sort((a, b) => new Date(b.date) - new Date(a.date))
              const groupedCompJobs = groupedCustomerJobs(compJobs)
              const lastJob = compJobs[0]
              return (
                <div key={comp.id} className="comp-card">
                  <div className="comp-card-top">
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 5 }}>
                        <span className={`badge badge-${comp.type}`}>{comp.type.charAt(0).toUpperCase() + comp.type.slice(1)}</span>
                        <span className={`badge badge-${comp.brand}`}>{comp.brand === 'rockshox' ? 'RockShox' : comp.brand.charAt(0).toUpperCase() + comp.brand.slice(1)}</span>
                        <span className="text-white fw7" style={{ fontSize: 14 }}>{comp.year ? comp.year + ' ' : ''}{comp.model}</span>
                      </div>
                      <div className="comp-meta">
                        {comp.serial ? 'Serial: ' + comp.serial : 'No serial registered'}
                        {comp.part_number ? ' · Part No: ' + comp.part_number : ''}
                        {comp.travel ? ' · ' + comp.travel : ''}
                      </div>
                      {comp.notes && <div className="comp-desc">{comp.notes.length > 120 ? comp.notes.slice(0, 120) + '…' : comp.notes}</div>}
                      {lastJob && <div className="fs12 mt4 text-muted">Last service: <span style={{ color: 'var(--light)' }}>{fmtDate(lastJob.date)}</span></div>}
                      {renderComponentSetup(comp)}
                      {renderServicePassportPreview(comp, compJobs)}
                    </div>
                    <div className="comp-actions">
                      {comp.brand === 'fox' && comp.serial && (
                        <a className="btn btn-ghost btn-sm" href={`https://tech.ridefox.com/bike/search/serial/${encodeURIComponent(comp.serial)}`} target="_blank" rel="noopener noreferrer">Fox Docs ↗</a>
                      )}
                      {comp.brand === 'rockshox' && (
                        <a className="btn btn-ghost btn-sm" href={rockShoxTrailheadUrl(comp.serial)} target="_blank" rel="noopener noreferrer">RockShox Trailhead</a>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={() => setCompModal(comp)}>Edit</button>
                      <button className="btn btn-green btn-sm" disabled={!canCreateJob} onClick={() => setIntakeModal(repeatIntakeSeed(c, lastJob, comp))}>{lastJob ? 'Book repeat work' : '+ Service Job'}</button>
                    </div>
                  </div>

                  {compJobs.length > 0 && (
                    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                      {renderCustomerJobGroups(groupedCompJobs, c, comp)}
                    </div>
                  )}
                </div>
              )
            })}

            {generalJobs.length > 0 && (
              <div className="comp-card" style={{ borderColor: 'var(--green)' }}>
                <div className="comp-card-top">
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 5 }}>
                      <span className="badge badge-other">General</span>
                      <span className="text-white fw7" style={{ fontSize: 14 }}>General Workshop Jobs</span>
                    </div>
                    <div className="comp-meta">Stored separately from suspension component service history.</div>
                  </div>
                </div>

                <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  {renderCustomerJobGroups(groupedCustomerJobs(generalJobs), c)}
                </div>
              </div>
            )}

            {unassignedJobs.length > 0 && (
              <div className="comp-card" style={{ borderColor: 'var(--yellow-line)' }}>
                <div className="comp-card-top">
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 5 }}>
                      <span className="badge badge-other">Booking</span>
                      <span className="text-white fw7" style={{ fontSize: 14 }}>Unassigned Jobs</span>
                    </div>
                    <div className="comp-meta">No component linked yet</div>
                  </div>
                  <div className="comp-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => openAddComponent(c, selectedBike)}>+ Add Component</button>
                  </div>
                </div>

                <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  {renderCustomerJobGroups(groupedCustomerJobs(unassignedJobs), c)}
                </div>
              </div>
            )}
            </div>
            )}
          </div>
        )
        })}
        </div>
      )}

      {addBikeCustomer && <AddBikeModal workshopId={workshopId} customer={addBikeCustomer} bikes={bikes}
        onClose={() => setAddBikeCustomer(null)} onSaved={savedBike => {
          if (activeBikeScope.current?.workshopId !== workshopId || activeBikeScope.current?.customerId !== addBikeCustomer.id
            || savedBike.workshop_id !== workshopId || savedBike.customer_id !== addBikeCustomer.id) return
          setBikes(current => [...current.filter(row => row.id !== savedBike.id), savedBike])
          setSelectedBikeByCustomer(current => ({ ...current, [savedBike.customer_id]: savedBike.id }))
          setAddBikeCustomer(null)
          load()
        }} />}
      {jobChoice && <div className="modal-overlay"><div className="modal" role="dialog" aria-modal="true" aria-label="Choose job type">
        <div className="modal-header"><div className="modal-title">Create a job</div><button className="close-btn" aria-label="Close job choice" onClick={() => setJobChoice(null)}>×</button></div>
        <p>{customerDisplayName(jobChoice.customer)}</p>
        <p>Intended bike: <strong>{jobChoice.bike ? bikeName(jobChoice.bike) : 'No bike linked'}</strong></p>
        <div className="action-row">
          <button className="btn btn-primary" disabled={!generalEnabled || !canCreateJob} onClick={() => { setGeneralIntake(jobChoice); setJobChoice(null) }}>General Workshop Job</button>
          <button className="btn btn-primary" disabled={!canCreateJob} onClick={() => {
            setIntakeModal({ customerId: jobChoice.customer.id, bikeId: jobChoice.bike?.id || '' })
            setJobChoice(null)
          }}>Suspension Service Job</button>
        </div>
        {!generalEnabled && <p className="text-muted fs12">General Workshop jobs are not enabled for this workshop.</p>}
      </div></div>}
      {generalIntake && <GeneralJobModal workshopId={workshopId} customers={customers} bikes={bikes} generalServices={generalJobServices}
        initialData={{ customer_id: generalIntake.customer.id, bike_id: generalIntake.bike?.id || '' }}
        canCreateGeneralJob={generalEnabled && canCreateJob} returnLabel="Back to Customers"
        onClose={() => setGeneralIntake(null)} onSaved={() => { setGeneralIntake(null); load() }} />}
      {/* Customer Modal */}
      {viewHistoricId && historicJobs.find(record => record.id === viewHistoricId) &&
        <LegacyHistoricJobModal record={historicJobs.find(record => record.id === viewHistoricId)}
          customers={customers} owner={profile?.role === 'owner'}
          onClose={() => setViewHistoricId(null)}
          onLinked={updated => setHistoricJobs(current => current.map(record =>
            record.id === updated.id ? updated : record))} />}

      {customerModal && (
        <CustomerModal
          workshopId={workshopId}
          initialData={isEditCustomer ? customerModal : null}
          onClose={() => setCustomerModal(null)}
          onSaved={() => { setCustomerModal(null); load() }}
          onDeleted={() => { setCustomerModal(null); load() }}
        />
      )}

      {/* Component Modal */}
      {compModal && (
        <ComponentModal
          workshopId={workshopId}
          customerId={isEditComp ? compModal.customer_id : compModal.customerId}
          initialData={isEditComp ? compModal : null}
          prefillData={!isEditComp ? compModal.prefillData : null}
          onClose={() => setCompModal(null)}
          onSaved={handleComponentSaved}
          onDeleted={() => { setCompModal(null); load() }}
        />
      )}

      {fileAccessCustomer && profile?.role === 'owner' && <CustomerFileAccessModal workshopId={workshopId} customer={fileAccessCustomer} onClose={() => setFileAccessCustomer(null)} />}

      {/* Fast Job Intake Modal */}
      {intakeModal && (
        <NewJobIntakeModal
          userId={profile?.id}
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          preCustomerId={intakeModal.customerId || ''}
          preBikeId={intakeModal.bikeId || ''}
          preComponentId={intakeModal.componentId || ''}
          preServiceIds={intakeModal.serviceIds || []}
          preRequestedWork={intakeModal.requestedWork || ''}
          returnLabel="Back to Customers"
          onClose={() => setIntakeModal(null)}
          onSaved={(job) => { setIntakeModal(null); if (job?.id) setViewJobId(job.id); load() }}
        />
      )}

      {/* Job Modal */}
      {jobModal && (
        <JobModal
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          generalServices={generalJobServices}
          preCustomerId={null}
          preComponentId={null}
          initialData={isEditJob ? jobModal : null}
          returnLabel="Back to Customers"
          onClose={() => setJobModal(null)}
          onChanged={load}
          onSaved={savedJob => { setJobModal(current => current?.id === savedJob?.id ? { ...current, ...savedJob } : current); load() }}
          onDeleted={() => { setJobModal(null); load() }}
        />
      )}

      {viewJobId && viewJob && (
        <ViewJobModal
          job={viewJob}
          customer={viewCust}
          component={viewComp}
          bike={viewBike}
          workshop={workshop}
          services={services}
          generalServices={generalJobServices}
          returnLabel="Back to Customers"
          onClose={() => setViewJobId(null)}
          onEdit={() => { setViewJobId(null); setJobModal(viewJob) }}
          onChanged={load}
          onCreateComponent={isSuspensionJob(viewJob) && !viewComp && viewCust ? () => { setViewJobId(null); createComponentFromJob(viewCust.id, viewJob) } : null}
        />
      )}
    </>
  )
}
